import { v4 } from "uuid";
import { ROUTES } from "../../constant";
import { APP_CONFIG } from "../../constant/common";
import { iGlobalContext } from "../../context/globalContext/interface";
import { iChatData } from "../../types/common";
import { resolveBoolFlag } from "../appConfig/resolveBoolFlag";
import { isChatOnDesktopScreen } from "../../component/reusableComponent/resultDetailScreen/resultDetailSurface";

export type ChatCheckoutKind = NonNullable<iChatData["chatCheckout"]>["kind"];

export type ChatCheckoutSelection = NonNullable<NonNullable<iChatData["chatCheckout"]>["selection"]>;

export type ChatBooking = NonNullable<iChatData["chatBooking"]>;

export const CHAT_CHECKOUT_PAGE_LOAD_ID = v4();

export const isChatCheckoutEnabled = (appConfig: iGlobalContext["appConfig"]): boolean =>
  resolveBoolFlag(appConfig, APP_CONFIG.CHAT_CHECKOUT_ENABLED);

export const shouldStartChatCheckout = (
  appConfig: iGlobalContext["appConfig"],
  isMobile: boolean,
  firstScreen: string,
  screen: string
): boolean => {
  const nativeElement = document.getElementById("iosNative") as HTMLInputElement | null;
  const isIOSNativeApp = nativeElement?.value === "true";
  return (
    isChatCheckoutEnabled(appConfig) &&
    !isIOSNativeApp &&
    (isMobile ? window.location.pathname === ROUTES.TopDestination : isChatOnDesktopScreen(firstScreen, screen))
  );
};

export const isLiveChatCheckout = (entry?: iChatData): boolean =>
  entry?.chatCheckout?.pageLoadId === CHAT_CHECKOUT_PAGE_LOAD_ID;

export const isUnbookedChatCheckout = (entry?: iChatData): boolean => isLiveChatCheckout(entry) && !entry?.chatBooking;

export const keptChatSelection = (
  chatData: iChatData[] | undefined,
  kind: ChatCheckoutKind,
  searchId?: string
): ChatCheckoutSelection | undefined => {
  const selection = (chatData ?? []).find((entry) => isLiveChatCheckout(entry) && entry.chatCheckout?.kind === kind)
    ?.chatCheckout?.selection;
  return selection && (!selection.searchId || selection.searchId === searchId) ? selection : undefined;
};

export const isChatCheckoutSurface = (element?: Element | null): boolean =>
  !!element?.hasAttribute("data-chat-checkout");

const scrollsVertically = (element: HTMLElement): boolean => {
  const { overflowY } = getComputedStyle(element);
  return (overflowY === "auto" || overflowY === "scroll") && element.scrollHeight > element.clientHeight + 1;
};

export const scrollChatCheckoutField = (field: HTMLElement) => {
  let scroller = field.parentElement;
  while (scroller && scroller !== document.body && !scrollsVertically(scroller)) scroller = scroller.parentElement;
  const margin = parseFloat(getComputedStyle(field).getPropertyValue("--chat-field-offset")) || 0;
  if (scroller && scroller !== document.body) {
    const top = field.getBoundingClientRect().top - scroller.getBoundingClientRect().top - margin;
    scroller.scrollBy({ top, behavior: "smooth" });
    return;
  }
  const top = field.getBoundingClientRect().top - margin;
  document.body.scrollBy({ top, behavior: "smooth" });
  window.scrollBy({ top, behavior: "smooth" });
};

export const withoutChatCheckout = (chatData: iChatData[]): iChatData[] =>
  chatData.some((entry) => entry?.chatCheckout)
    ? chatData.flatMap((entry) => {
        if (!entry?.chatCheckout) return [entry];
        if (!entry.chatBooking) return [];
        const { chatCheckout, ...booked } = entry;
        return [booked];
      })
    : chatData;

const chatCardEntry = (): iChatData => ({
  id: v4(),
  message: "",
  prompt: [],
  isUserMessage: false,
  isLoading: false,
  destinationList: [],
  singleSelectedDestination: false,
});

export const withChatCheckout = (
  chatData: iChatData[],
  kind: ChatCheckoutKind,
  selection?: ChatCheckoutSelection
): iChatData[] => [
  ...withoutChatCheckout(chatData),
  {
    ...chatCardEntry(),
    chatCheckout: { kind, pageLoadId: CHAT_CHECKOUT_PAGE_LOAD_ID, ...(selection?.resultId ? { selection } : {}) },
  },
];

export const withChatBooking = (chatData: iChatData[], entryId: string, booking: ChatBooking): iChatData[] =>
  chatData.some((entry) => entry?.id === entryId)
    ? chatData.map((entry) => (entry?.id === entryId ? { ...entry, chatBooking: booking } : entry))
    : [...chatData, { ...chatCardEntry(), chatBooking: booking }];
